import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { Pill } from 'components/ManagementCard'
import { Textarea } from 'components/Textarea'
import { TermsDocument } from 'components/TermsDocument'
import { routes } from 'constants/routes'
import { termsManagementStrings } from 'constants/strings'
import { toErrorMessage } from 'hooks/utils'
import {
  useCreateTermsDraft,
  usePublishTermsVersion,
  useTermsPolicy,
  useTermsVersion,
  useUpdateTermsDraft,
} from 'hooks/useTermsAdmin'
import type { TTermsDraftRequest, TTermsVersionDetail } from 'types/terms'
import { formatDateTime } from 'utils'
import { buildTermsVersionPath } from './index'
import { PublishDialog } from './PublishDialog'

const strings = termsManagementStrings.editor

const EMPTY_DRAFT: TTermsDraftRequest = {
  serviceName: '',
  title: '',
  versionLabel: '',
  bodyMarkdown: '',
}

const toDraft = (version: TTermsVersionDetail): TTermsDraftRequest => ({
  serviceName: version.serviceName,
  title: version.title,
  versionLabel: version.versionLabel,
  bodyMarkdown: version.bodyMarkdown,
})

const isSameDraft = (a: TTermsDraftRequest, b: TTermsDraftRequest) =>
  (Object.keys(a) as Array<keyof TTermsDraftRequest>).every(
    (key) => a[key] === b[key],
  )

/** 送信前の検証。サーバー側の DTO と同じ条件（空白だけの名称・本文を拒否） */
const validateDraft = (draft: TTermsDraftRequest): string | undefined => {
  if (
    !draft.serviceName.trim() ||
    !draft.title.trim() ||
    !draft.versionLabel.trim() ||
    !draft.bodyMarkdown.trim()
  ) {
    return strings.errors.required
  }
  return undefined
}

export default function TermsManagementEditor() {
  const navigate = useNavigate()
  const { versionId: versionIdParam } = useParams()
  const versionId = versionIdParam ? Number(versionIdParam) : undefined
  const isCreate = versionId === undefined
  const hasValidId = isCreate || (Number.isInteger(versionId) && (versionId ?? 0) > 0)

  const version = useTermsVersion(hasValidId && !isCreate ? versionId : undefined)
  const policy = useTermsPolicy()
  const createDraft = useCreateTermsDraft()
  const updateDraft = useUpdateTermsDraft()
  const publish = usePublishTermsVersion()

  const [draft, setDraft] = useState<TTermsDraftRequest>(EMPTY_DRAFT)
  // 入力の元にしたサーバーの版。画面の表示と、保存・公開で送る editRevision はこの版に合わせる
  const [base, setBase] = useState<TTermsVersionDetail>()
  const [validationError, setValidationError] = useState<string | undefined>()
  const [notice, setNotice] = useState<string | undefined>()
  const [publishOpen, setPublishOpen] = useState(false)

  const isSaving = createDraft.isPending || updateDraft.isPending
  const latest = version.data
  // 同じ版の取得結果が入力の元より古い（保存後の取り直しの失敗・保存前に始まった取得の応答）。
  // 保存済みの内容・revision を古い版へ戻さない
  const isStale =
    latest !== undefined &&
    base !== undefined &&
    latest.id === base.id &&
    latest.editRevision < base.editRevision
  // 取得した最新の内容へ合わせる。ただし同じ版に未保存の入力がある間は自動で置き換えない
  // （画面復帰時の再取得などで、長い本文の編集中の内容を黙って消さない）
  if (
    latest &&
    latest !== base &&
    !isStale &&
    (base === undefined ||
      base.id !== latest.id ||
      isSameDraft(draft, toDraft(base)))
  ) {
    setBase(latest)
    setDraft(toDraft(latest))
  }
  // 編集中に他の操作で保存・公開された（届いた最新の内容が入力の元より新しい）。
  // 自分の保存中は、反映した保存結果を入力の元にする前なので判定しない（他の操作との競合は 409 で分かる）
  const hasConflict =
    !isSaving &&
    latest !== undefined &&
    base !== undefined &&
    latest.id === base.id &&
    !isStale &&
    (latest.editRevision !== base.editRevision || latest.status !== base.status)

  const isPublished = base?.status === 'PUBLISHED'
  const savedDraft = useMemo(
    () => (base ? toDraft(base) : EMPTY_DRAFT),
    [base],
  )
  const hasUnsavedChanges = isCreate
    ? !isSameDraft(draft, EMPTY_DRAFT)
    : !isSameDraft(draft, savedDraft)
  const mutationError =
    createDraft.error || updateDraft.error
      ? toErrorMessage(
          createDraft.error ?? updateDraft.error,
          strings.errors.saveFailed,
        )
      : publish.error
        ? toErrorMessage(publish.error, strings.errors.publishFailed)
        : undefined
  const loadError =
    !hasValidId || (!isCreate && !version.isPending && !version.data)
      ? version.error
        ? toErrorMessage(version.error, strings.errors.loadFailed)
        : strings.errors.notFound
      : undefined
  const alertMessage = validationError || mutationError || loadError

  const setField = (key: keyof TTermsDraftRequest, value: string) =>
    setDraft((current) => ({ ...current, [key]: value }))

  /** 利用者が明示的に選んだときだけ、編集中の内容を破棄して最新の内容を読み込む */
  const reloadLatest = () => {
    if (!latest) return
    setBase(latest)
    setDraft(toDraft(latest))
    setValidationError(undefined)
    setNotice(undefined)
    updateDraft.reset()
  }

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSaving || isPublished || hasConflict) return
    setNotice(undefined)
    const error = validateDraft(draft)
    setValidationError(error)
    if (error) return

    if (isCreate) {
      const created = await createDraft.mutateAsync(draft).catch(() => undefined)
      if (created) {
        navigate(buildTermsVersionPath(created.id), { replace: true })
      }
      return
    }
    if (!base) return
    const updated = await updateDraft
      .mutateAsync({
        versionId: base.id,
        request: { ...draft, expectedEditRevision: base.editRevision },
      })
      .catch(() => undefined)
    if (updated) {
      // 保存した内容（サーバーで整形済み）を新しい入力の元にする
      setBase(updated)
      setDraft(toDraft(updated))
      setNotice(strings.saved)
    }
  }

  const handlePublish = async (reconsentRequired: boolean) => {
    if (!base || !policy.data) return
    const published = await publish
      .mutateAsync({
        versionId: base.id,
        request: {
          reconsentRequired,
          expectedPolicyRevision: policy.data.revision,
          // プレビュー（保存済みの内容）と同じ版だけを公開する
          expectedEditRevision: base.editRevision,
        },
      })
      .catch(() => undefined)
    setPublishOpen(false)
    if (published) {
      navigate(routes.TERMS_MANAGEMENT, {
        state: { snackbarMessage: strings.published },
      })
    }
  }

  const title = isCreate
    ? strings.createTitle
    : isPublished
      ? strings.viewTitle
      : strings.editTitle
  const fieldsDisabled = isPublished || isSaving || (!isCreate && !base)

  return (
    <ManagementFormLayout
      kicker={strings.createKicker}
      title={title}
      description={strings.description}
      backTo={routes.TERMS_MANAGEMENT}
      maxWidthClassName="max-w-[1180px]"
    >
      {alertMessage && <Alert>{alertMessage}</Alert>}
      {hasConflict && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <span>{strings.conflict}</span>
          <Button type="button" size="sm" variant="outline" onClick={reloadLatest}>
            {strings.reloadLatest}
          </Button>
        </div>
      )}
      {notice && (
        <div
          role="status"
          className="rounded-2xl border border-accent/20 bg-accent-soft px-4 py-3 text-sm text-ink"
        >
          {notice}
        </div>
      )}
      {isPublished && base && (
        <div className="grid gap-1 rounded-2xl border border-border/70 bg-surface px-4 py-3 text-sm text-ink">
          <span className="flex flex-wrap items-center gap-2">
            <Pill tone={base.isActive ? 'success' : 'neutral'}>
              {base.isActive
                ? termsManagementStrings.versions.status.active
                : termsManagementStrings.versions.status.published}
            </Pill>
            {strings.publishedMeta}: {formatDateTime(base.publishedAt)}{' '}
            {base.publishedByName ?? ''}
          </span>
          <span className="text-xs text-muted">{strings.publishedNotice}</span>
          {base.bodyHash && (
            <span className="break-all text-[11px] text-muted">
              {strings.hashLabel}: {base.bodyHash}
            </span>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <form
          className="grid content-start gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
          onSubmit={(event) => {
            void handleSave(event)
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.fields.serviceName}
              <Input
                name="serviceName"
                placeholder={strings.fields.serviceNamePlaceholder}
                value={draft.serviceName}
                disabled={fieldsDisabled}
                maxLength={100}
                onChange={(event) => setField('serviceName', event.target.value)}
              />
            </label>
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.fields.versionLabel}
              <Input
                name="versionLabel"
                placeholder={strings.fields.versionLabelPlaceholder}
                value={draft.versionLabel}
                disabled={fieldsDisabled}
                maxLength={100}
                onChange={(event) => setField('versionLabel', event.target.value)}
              />
            </label>
          </div>
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.fields.title}
            <Input
              name="title"
              placeholder={strings.fields.titlePlaceholder}
              value={draft.title}
              disabled={fieldsDisabled}
              maxLength={200}
              onChange={(event) => setField('title', event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.fields.bodyMarkdown}
            <Textarea
              name="bodyMarkdown"
              className="min-h-[420px] font-mono text-xs"
              value={draft.bodyMarkdown}
              disabled={fieldsDisabled}
              onChange={(event) => setField('bodyMarkdown', event.target.value)}
            />
            <span className="text-[11px] font-medium text-muted">
              {strings.fields.bodyHelper}
            </span>
          </label>
          {!isPublished && (
            <div className="flex flex-wrap items-center justify-end gap-3">
              {!isCreate && hasUnsavedChanges && (
                <span className="text-xs text-muted">
                  {strings.unsavedBeforePublish}
                </span>
              )}
              <Button
                type="submit"
                variant="outline"
                loading={isSaving}
                disabled={!hasUnsavedChanges || hasConflict}
              >
                {strings.save}
              </Button>
              {!isCreate && (
                <Button
                  type="button"
                  disabled={hasUnsavedChanges || !base || !policy.data || isSaving}
                  onClick={() => setPublishOpen(true)}
                >
                  {strings.publish}
                </Button>
              )}
            </div>
          )}
        </form>

        <section className="grid content-start gap-3 rounded-2xl border border-border/70 bg-surface/60 p-5">
          <h2 className="text-sm font-semibold text-ink">{strings.preview}</h2>
          {draft.bodyMarkdown.trim() ? (
            <TermsDocument
              serviceName={draft.serviceName}
              title={draft.title}
              versionLabel={draft.versionLabel}
              bodyMarkdown={draft.bodyMarkdown}
              publishedAt={base?.publishedAt}
            />
          ) : (
            <p className="text-sm text-muted">{strings.previewEmpty}</p>
          )}
        </section>
      </div>

      <PublishDialog
        open={publishOpen}
        isFirstPublication={policy.data?.activeVersion === null}
        isPublishing={publish.isPending}
        onClose={() => setPublishOpen(false)}
        onConfirm={(reconsentRequired) => {
          void handlePublish(reconsentRequired)
        }}
      />
    </ManagementFormLayout>
  )
}
